import { FC, useEffect, useMemo } from 'react';
import { PetDetails } from '@weave/schema-gen-ts/dist/schemas/pets-api/v1/pets_api.pb';
import { useTranslation } from '@frontend/i18n';
import { ChecklistField, Modal, ModalControlModalProps, Text, useForm } from '@frontend/design-system';
import { generateTrackingId } from '../../utils';
import { getPetPmsServiceableId } from './utils';

interface SyncPetsModalProps extends ModalControlModalProps {
  petList: PetDetails[];
  isDemo?: boolean;
  onSyncClick: (pmsIds: string[]) => void;
}

const COMPONENT_ID = 'sync-pets-modal';

export const SyncPetsModal: FC<SyncPetsModalProps> = ({ petList, isDemo, onSyncClick, ...modalProps }) => {
  const { t } = useTranslation('call-intelligence');

  const { getFieldProps, values, reset } = useForm({
    fields: {
      pets: {
        type: 'checklist',
        value: [],
      },
    },
  });

  const options = useMemo(
    () =>
      petList.map((pet) => {
        const pmsId = getPetPmsServiceableId(pet);
        return {
          label: pet.name,
          value: pmsId,
        };
      }),
    [petList]
  );

  useEffect(() => {
    if (!modalProps.show) {
      reset();
    }
  }, [modalProps.show]);

  return (
    <Modal {...modalProps} minWidth={400}>
      <Modal.Header
        onClose={modalProps.onClose}
        closeBtnTrackingId={generateTrackingId({
          component: COMPONENT_ID,
          context: 'retry-sync-button',
          isDemoAccount: isDemo,
        })}
      >
        {t('Select a Pet')}
      </Modal.Header>
      <Modal.Body>
        <Text size='medium'>{t('Select a pet to sync this summary.')}</Text>
        <ChecklistField label='' {...getFieldProps('pets')}>
          {options.map((option) => (
            <ChecklistField.Option key={option.value} name={option.value}>
              {option.label}
            </ChecklistField.Option>
          ))}
        </ChecklistField>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Sync Summary'),
          onClick: () => {
            onSyncClick(values.pets ?? []);
            modalProps.onClose();
          },
          trackingId: generateTrackingId({
            component: COMPONENT_ID,
            context: 'confirm-sync-button',
            isDemoAccount: isDemo,
          }),
          disabled: (values.pets ?? []).length === 0,
        }}
        secondary={{
          label: t('Cancel'),
          onClick: modalProps.onClose,
          trackingId: generateTrackingId({
            component: COMPONENT_ID,
            context: 'cancel-sync-button',
            isDemoAccount: isDemo,
          }),
        }}
      />
    </Modal>
  );
};
